/**
 * Legacy URLs retired by the navigation consolidation, and where they now go.
 *
 * This is the single source of truth: App.tsx renders its redirect routes
 * via `legacyRedirectRoutes()` below, so a destination cannot drift between
 * the app and its tests. See docs/plans/2026-09-14-admin-nav-consolidation-design.md
 */
import type { ReactElement } from 'react';
import { Navigate, Route } from 'react-router-dom';
import { ROUTES } from './constants';

export interface LegacyRedirect {
  from: string;
  to: string;
}

export const LEGACY_REDIRECTS: LegacyRedirect[] = [
  // /settings is the Git credentials tab, which is what the old /profile page
  // mostly was: an email line above the same SettingsForm. Sending it to the
  // new read-only Profile tab would drop the part people went there for.
  { from: ROUTES.LEGACY_PROFILE, to: ROUTES.SETTINGS },
  { from: ROUTES.LEGACY_FEATURES, to: ROUTES.SETTINGS_FEATURES },
  { from: ROUTES.LEGACY_ADMIN_FEATURES, to: ROUTES.ADMIN_REGISTRY },
  // The old /admin/projects rendered the assignments screen, not packs, so it
  // lands on the Projects tab rather than on the Provisioning index.
  { from: ROUTES.LEGACY_ADMIN_PROJECTS, to: ROUTES.ADMIN_PROVISIONING_PROJECTS },
  { from: ROUTES.LEGACY_ADMIN_PACKS, to: ROUTES.ADMIN_PROVISIONING },
  { from: ROUTES.LEGACY_ADMIN_IMAGES, to: ROUTES.ADMIN_REGISTRY_IMAGES },
  { from: ROUTES.LEGACY_ADMIN_GIT_SERVERS, to: ROUTES.ADMIN_SYSTEM },
  { from: ROUTES.LEGACY_ADMIN_LOGIN_PROVIDERS, to: ROUTES.ADMIN_SYSTEM_LOGINS },
];

/**
 * The actual redirect <Route> elements, generated from LEGACY_REDIRECTS.
 *
 * Production code (App.tsx) renders this directly, so a test that renders it
 * inside its own <Routes> tree exercises the real wiring — deleting the call
 * in App.tsx would fail that test, unlike a test that rebuilds its own route
 * list from the table.
 */
export function legacyRedirectRoutes(): ReactElement[] {
  return LEGACY_REDIRECTS.map(({ from, to }) => (
    <Route key={from} path={from} element={<Navigate to={to} replace />} />
  ));
}
